import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { TFasterPayoutsFiltersForm } from './types';
import { useTranslation } from 'react-i18next';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { TextStyles } from '@payler/ui-theme';
import { useEffect, useMemo } from 'react';
import {
  createDateFieldResolver,
  FloatingInputField,
  ThinDivider,
} from '@payler/ui-components';
import { FiltersOptionsArrayAccordion } from '../../components/FiltersOptionsArrayAccordion/FiltersOptionsArrayAccordion';
import { FiltersDateRangeAccordion } from '../../components/FiltersDateRangeAccordion/FiltersDateRangeAccordion';
import { FiltersAmountRangeAccordion } from '../../components/FiltersAmountRangeAccordion/FiltersAmountRangeAccordion';
import { FiltersInputAccordion } from '../../components/FiltersInputAccordion/FiltersInputAccordion';

import createLogger from 'debug';
import { Templates } from './Templates';
import { useCloseFasterPayoutsModal } from '../../modals/FasterPayoutsFiltersModal';
import isEqual from 'lodash/isEqual';
import { FPS_CURRENCY_OPTIONS, FPS_FILTERS_STATUS_OPTIONS } from '../../const';
import { FPS_SOURCE_OPTIONS } from '../../hooks/faster-payouts/constants';
import {
  useFasterPayoutsFiltersFromUrl,
  useSetFasterPayoutsUrlSearchParams,
} from '../../hooks/faster-payouts/url';
import { useSaveFasterPayoutsTemplate } from '../../hooks/faster-payouts/templates';
import { isNil, omitBy } from 'lodash';
import { useResetFormAndUrlFilters } from '../../hooks/use-reset-form-and-url-filters';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { useUIDateFormat } from '../../hooks/use-ui-dateFormat';

const log = createLogger('FasterPayoutsFilters');

const defaultValues: TFasterPayoutsFiltersForm = {
  Currency: null,
  DateFrom: null,
  DateTo: null,
  AmountFrom: null,
  AmountTo: null,
  Statuses: [],
  Rrn: null,
  OrderId: null,
  Source: 'any',
};

const useCreateResolver = () => {
  const { t } = useTranslation();
  const dateFormat = useUIDateFormat();
  return () => {
    return yupResolver(
      yup.object({
        DateFrom: createDateFieldResolver(t, {
          toFieldName: 'DateTo',
          maxDate: dayjs(),
          dateFormat,
        }),
        DateTo: createDateFieldResolver(t, {
          fromFieldName: 'DateFrom',
          maxDate: dayjs(),
          dateFormat,
        }),
      }),
    );
  };
};

//region form
export const FasterPayoutsFiltersForm = () => {
  const filters = useFasterPayoutsFiltersFromUrl();
  const closeModal = useCloseFasterPayoutsModal();
  const createResolver = useCreateResolver();
  const methods = useForm<TFasterPayoutsFiltersForm>({
    defaultValues,
    resolver: createResolver(),
  });
  const { t } = useTranslation(['fields']);
  const setFilters = useSetFasterPayoutsUrlSearchParams();

  const handleSubmit = methods.handleSubmit((values) => {
    log('handleSubmit', values);
    setFilters(values);
    closeModal();
  });

  useEffect(() => {
    methods.reset(filters);
    log('resetting form, filters: %O', filters);
  }, [methods, filters]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <VStack alignItems="stretch" spacing={0} divider={<ThinDivider />}>
          <Header />
          <Templates />
          <Box p={2}>
            <FloatingInputField
              name="OrderId"
              label={t('fields:transactionNumber')}
            />
          </Box>
          <RRN />
          <Source />
          <Status />
          <Currency />
          <Dates />
          <Amount />
          <Buttons />
        </VStack>
      </form>
    </FormProvider>
  );
};
//endregion

//region fields
const Amount = () => (
  <FiltersAmountRangeAccordion
    formFieldFrom="AmountFrom"
    formFieldTo="AmountTo"
    fromKey="fields:from"
    toKey="fields:to"
    titleKey="fields:amount"
  />
);

const Dates = () => (
  <FiltersDateRangeAccordion
    formFieldFrom="DateFrom"
    formFieldTo="DateTo"
    fromKey="fields:from"
    toKey="fields:to"
    titleKey="fields:period"
    dateFormat="DD.MM.YYYY"
  />
);

const Source = () => {
  const { watch, setValue } = useFormContext<TFasterPayoutsFiltersForm>();
  const source = watch('Source');

  useEffect(() => {
    if (!source) {
      setValue('Source', 'any', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [source, setValue]);

  return (
    <FiltersOptionsArrayAccordion
      formFieldName={'Source' as never}
      fieldNameKey="fields:fasterPayoutSource"
      options={[...FPS_SOURCE_OPTIONS]}
      optionsPrefix="fields:fasterPayoutSource."
      hideClearIcon={source === 'any'}
    />
  );
};

const Status = () => (
  <FiltersOptionsArrayAccordion
    formFieldName="Statuses"
    fieldNameKey="fields:paymentStatus"
    options={FPS_FILTERS_STATUS_OPTIONS}
    optionsPrefix="fasterPayouts:status."
    multi
  />
);

const Currency = () => (
  <FiltersOptionsArrayAccordion
    formFieldName="Currency"
    fieldNameKey="fields:currency"
    options={FPS_CURRENCY_OPTIONS}
    optionsPrefix="currency:"
  />
);

const RRN = () => {
  const { t } = useTranslation(['fields']);
  return (
    <FiltersInputAccordion
      fieldNameKey="rrn"
      formFieldName="Rrn"
      placeholder={t('fields:rrnPlaceholder')}
      inputType="text"
    />
  );
};
//endregion

//region count selected
const arrayAndNotEmpty = (val: unknown) => Array.isArray(val) && val.length > 0;
const useFiltersCount = () => {
  const { watch } = useFormContext<TFasterPayoutsFiltersForm>();
  const data = watch();
  return useMemo(() => {
    let count = 0;
    count += data.AmountFrom || data.AmountTo ? 1 : 0;
    count += data.DateFrom || data.DateTo ? 1 : 0;
    count += arrayAndNotEmpty(data.Statuses) ? 1 : 0;
    count += data.Currency ? 1 : 0;
    count += data.Rrn ? 1 : 0;
    count += data.Source && data.Source !== 'any' ? 1 : 0;
    return count;
  }, [data]);
};
//endregion

//region header
const Header = () => {
  const { t } = useTranslation(['filters']);
  const reset = useResetFormAndUrlFilters();
  const count = useFiltersCount();

  return (
    <HStack justifyContent="space-between">
      <Box pl={3} pr={2} py={2} alignSelf="flex-start">
        <Text as="h4" textStyle={TextStyles.h4} color="primary.500">
          {t('filters:title')}
        </Text>
        <Text textStyle={TextStyles.Subtitle14Regular} color="primary.400">
          {count} {t('filters:selected')}
        </Text>
      </Box>
      <Box pr={3}>
        <Button variant="secondary" onClick={reset}>
          {t('filters:reset')}
        </Button>
      </Box>
    </HStack>
  );
};
//endregion

//region buttons
const isEmpty = (val: unknown) =>
  isNil(val) || (Array.isArray(val) && val.length === 0);

const Buttons = () => {
  const filters = useFasterPayoutsFiltersFromUrl();
  const { t } = useTranslation(['filters']);
  const count = useFiltersCount();
  const { getValues, watch } = useFormContext<TFasterPayoutsFiltersForm>();
  const save = useSaveFasterPayoutsTemplate();
  const handleSave = () => {
    save.mutate(getValues());
  };

  const applyDisabled = isEqual(
    omitBy(filters, isEmpty),
    omitBy(watch(), isEmpty),
  );

  return (
    <VStack p={2} alignItems="center" spacing="10px">
      <Button type="submit" w="100%" disabled={applyDisabled}>
        {t('filters:submit')}
      </Button>
      <Button
        variant="link"
        disabled={!count || save.isPending}
        onClick={handleSave}
      >
        {t('filters:save')}
      </Button>
    </VStack>
  );
};
//endregion
